import dayjs from "dayjs";
import { ProjectT, SharedVideoDataT } from "../models/models";
import correctVersionFileType from "../utils/helpers/correctVersionFileType";
import { buildManagePageQueryString } from "./helpers/buildManagePageQueryString";
import { jumboMediaAPI } from "./service";

export const projectsApi = jumboMediaAPI.injectEndpoints({
  endpoints: (build) => ({
    getProjects: build.query({
      query: (body) => ({
        url: `api/Projects/UserGet?itemsPerPage=${
          body?.limit || 10
        }&pageNumber=${body?.page - 1 || 0}&sortingOrder=${
          body?.sort || "num_desc"
        }&filterType=${body?.filterType || "name"}&filter=${
          body?.filter || ""
        }&statusName=${body?.statusName || ""}&projectType=${
          body?.projectType || "VideoAd"
        }&filterId=${body?.filterId || null}`,
      }),
      providesTags: ["Projects"],
    }),
    getUnfinishedProjects: build.query({
      query: () => ({
        url: `api/Projects/UserGetCampaignsByDeliveryUser`,
      }),
      providesTags: ["Projects"],
    }),
    getProjectById: build.query<
      ProjectT,
      { id: string | number; detailed?: boolean }
    >({
      query: ({ id, detailed = true }) => ({
        url: `api/Projects/UserGet/${id}?detailed=${detailed}`,
        responseHandler: (res) =>
          res.json().then((res) => ({
            ...res,
            Versions: res.Versions.map(correctVersionFileType),
          })),
      }),
      providesTags: ["Projects"],
    }),
    getProjectsTotalCount: build.query({
      query: () => "api/Projects/UserGetOverview/VideoAd?projectType=VideoAd",
    }),
    getManagePageProjects: build.query({
      query: (body) => ({
        url: `/api/Projects/AdminGetProjectsPartally?pageNumber=${
          body?.page - 1 || 0
        }&itemsPerPage=${body?.limit || 10}&filterType=${
          body?.filterType || "name"
        }&filter=${body?.filter || ""}&sortingOrder=${
          body?.isDescSortDir ? "num_desc" : "num_asc"
        }&statusName=${body?.statusName || ""}&projectType=${
          body?.projectType || "All"
        }`,
        responseHandler: (res) =>
          res.json().then((res) => ({
            data: res.Projects,
            recordsFiltered: res.ItemCount,
          })),
      }),
      providesTags: ["Projects"],
    }),
    getAllProjects: build.query({
      query: (body) => ({
        // url: `/api/Projects/AdminGet`,
        url: `/api/Projects/AdminGetWithValue?value=${body?.filter || ""}`,
        responseHandler: (res) =>
          res.json().then((res: ProjectT[]) => {
            if (body?.asSelectOptions) {
              return res.map((item) => ({ value: item.Id, label: item.Name }));
            } else {
              return res.map((item) => ({ Id: item.Id, Name: item.Name }));
            }
          }),
      }),
    }),
    createProject: build.mutation({
      query: (body) => ({
        url: "/api/Projects/UserPost",
        method: "POST",
        body: { ...body, IsReactProject: true },
      }),
      invalidatesTags: ["Projects"],
    }),
    createAndSendProject: build.mutation({
      query: (body) => ({
        url: "/api/Projects/UserPostAndSend",
        method: "POST",
        body: { ...body, IsReactProject: true },
      }),
      invalidatesTags: ["Projects"],
    }),
    createAndSendDeliveryService: build.mutation({
      query: (body) => ({
        url: "/api/Projects/UserPostAndSendDeliveryService",
        method: "POST",
        body: { ...body, IsReactProject: true },
      }),
      invalidatesTags: ["Projects"],
    }),
    setStartProjectEditing: build.mutation({
      query: (body) => ({
        url: `/api/Projects/UserStartEditing/${body.projectId}`,
        method: "POST",
      }),
      invalidatesTags: ["Projects"],
    }),
    setCancelProjectEditing: build.mutation({
      query: (body) => ({
        url: `/api/Projects/UserCancelEdit/${body.projectId}`,
        method: "PUT",
      }),
      invalidatesTags: ["Projects"],
    }),
    updateProjectStatus: build.mutation({
      query: (body) => ({
        url: "/api/Projects/AdminPostStatusUpdate",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Projects"],
    }),
    updateProject: build.mutation({
      query: (body) => ({
        url: `/api/Projects/UserPut/${body.Id}`,
        method: "PUT",
        body: { ...body, IsReactProject: true },
      }),
      invalidatesTags: ["Projects"],
    }),
    updateProjectShort: build.mutation({
      query: (body) => ({
        url: `/api/Projects/UserPutShort/${body.Id}`,
        method: "PUT",
        body: { ...body, IsReactProject: true },
      }),
      invalidatesTags: ["Projects"],
    }),
    updateAndSendProject: build.mutation({
      query: (body) => ({
        url: `/api/Projects/UserPutAndSend/${body.Id}`,
        method: "PUT",
        body: { ...body, IsReactProject: true },
      }),
      invalidatesTags: ["Projects"],
    }),
    deleteProject: build.mutation({
      query: (body) => ({
        url: `/api/Projects/UserDelete/${body.Id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Projects"],
    }),
    getActivityLog: build.query({
      query: (body) =>
        `/api/Projects/UserGetActivityLog?shareToken=${encodeURIComponent(
          body.shareToken || ""
        )}&projectId=${body.projectId}&pageNumber=${
          body.pageNumber || 0
        }&itemsPerPage=${body.itemsPerPage || 20}`,
      providesTags: ["ActivityLog"],
    }),
    updateActivityLog: build.mutation({
      query: (body) => ({
        url: `/api/Projects/UserPostActivityLog`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["ActivityLog"],
    }),
    getDownloadLog: build.query({
      query: (body) => ({
        url: `/api/Projects/UserGetDownloadLog?shareToken=${encodeURIComponent(
          body.shareToken || ""
        )}&projectId=${body.projectId}&pageNumber=${
          body.pageNumber || 0
        }&itemsPerPage=${body.itemsPerPage || 20}`,
      }),
    }),
    shareVideo: build.mutation({
      query: ({ Id, ...body }) => ({
        url: `/api/Projects/UserShareVideo/${Id}`,
        method: "POST",
        body,
      }),
    }),
    setHouseNumber: build.mutation({
      query: ({ Id, ...body }) => ({
        url: `/api/Projects/UserSetBroadcastingInfo/${Id}`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["ActivityLog", "Projects"],
    }),
    sendToFTP: build.mutation({
      query: (body) => ({
        url: `/api/Projects/UserSendFileToFTP/${body.Id}?is_second_slut=${
          body.asSponsorship || false
        }&confirmed=${body?.confirmed || "false"}`,
        method: "POST",
      }),
      invalidatesTags: ["ActivityLog"],
    }),
    sendToBroadcasters: build.mutation({
      query: ({ Id, ...body }) => ({
        url: `/api/Projects/UserSendProject/${Id}`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["ActivityLog", "Projects"],
    }),
    sendForCustomerApproval: build.mutation({
      query: ({ Id, ...body }) => ({
        url: `/api/Projects/UserShareProject/${Id}`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["ActivityLog", "Projects"],
    }),
    shareDelivery: build.mutation({
      query: ({ Id, ...body }) => ({
        url: `/api/Projects/UserSharePreviewProject/${Id}`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["ActivityLog", "Projects"],
    }),
    shareCampaign: build.mutation({
      query: ({ Id, ...body }) => ({
        url: `/api/Projects/UserShareCampaign/${Id}`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["ActivityLog", "Projects"],
    }),
    cancelProject: build.mutation({
      query: ({ Id }) => ({
        url: `/api/Projects/UserChangeStatus/${Id}?status=Cancelled`,
        method: "POST",
      }),
      invalidatesTags: ["ActivityLog", "Projects"],
    }),
    rejectProject: build.mutation({
      query: ({ Id }) => ({
        url: `/api/Projects/UserChangeStatus/${Id}?status=Rejected`,
        method: "POST",
      }),
      invalidatesTags: ["ActivityLog", "Projects"],
    }),
    closeProject: build.mutation({
      query: ({ Id }) => ({
        url: `/api/Projects/UserCloseProjectByBroadcaster/${Id}`,
        method: "POST",
      }),
      invalidatesTags: ["ActivityLog"],
    }),
    changeInternalNumber: build.mutation({
      query: ({ Id, ...body }) => ({
        url: `/api/Projects/UserSetInternalNumber/${Id}`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["ActivityLog", "Projects"],
    }),
    sendToAdAgency: build.mutation({
      query: ({ Id }) => ({
        url: `/api/Projects/UserChangeStatus/${Id}?status=WaitingForApprovalOfAdvertisingAgency`,
        method: "POST",
      }),
      invalidatesTags: ["Projects", "ActivityLog"],
    }),
    setFileUploaded: build.mutation({
      query: (body) => ({
        url: `/api/Projects/FileUploaded`,
        method: "POST",
        body,
        // headers: {
        //   "x-amz-storage-class": "INTELLIGENT_TIERING",
        // },
      }),
      invalidatesTags: ["Projects", "ActivityLog"],
    }),
    deleteUploadedFile: build.mutation({
      query: (body) => ({
        url: `/api/Projects/UserDeleteUploadedFile/${body.fileId}`,
        method: "POST",
      }),
      invalidatesTags: ["ActivityLog"],
    }),
    getSharedVideoData: build.query<SharedVideoDataT, { token: string }>({
      query: (body) => ({
        url: `/api/Projects/UserGetSharedVideoInfo/${body.token}`,
      }),
    }),
    getVideoShareLink: build.query<string, { fileId: number }>({
      query: ({ fileId }) => ({
        url: `/api/Projects/GetVideoShareLink?fileId=${fileId}`,
      }),
    }),
    changeProjectStatus: build.mutation({
      query: (body) => ({
        url: `/api/Projects/UserChangeStatus/${body.id}?status=${
          body.status || "WaitingForApprovalOfAdvertisingAgency"
        }`,
        method: "POST",
      }),
      invalidatesTags: ["Projects"],
    }),
    userDeliverySendProject: build.query({
      query: (body) => ({
        url: `/api/Projects/UserDeliverySendProject/${body.id}`,
        method: "GET",
        // body
      }),
    }),
    downloadFile: build.query({
      query: ({ id, shareToken = "" }) => ({
        url: `/api/Projects/DownloadFile/${id}?shareToken=${encodeURIComponent(shareToken)}`,
      }),
    }),
    sendFileToProcess: build.query({
      query: (body) => ({
        url: `/api/Projects/sendFileToProcess/${body.id}`,
        method: "GET",
      }),
    }),
    getNotificationsAmount: build.query({
      query: () => ({
        url: `/api/Projects/GetNotificationsNum?videoAdNotifTime=${dayjs()
          .subtract(3, "months")
          .toISOString()}&audioAdNotifTime=&projectType=both`,
      }),
    }),
    getProjectCreationInfo: build.query({
      query: () => ({
        url: `/api/Projects/GetProjectCreationInfo`,
      }),
    }),
    // spec-28 — the confirmation page is reached by an OPAQUE token instead of the raw sequential
    // projectId. `body.id` is whatever the route gave us, so send it as `token` when it is not a
    // plain number and as `projectId` when it is. Both forms are accepted by the backend and are
    // authorized identically, so old links (bookmarks, the admin grid's window.open, anything already
    // sent to a customer) keep working unchanged.
    getOrderData: build.query({
      query: (body) => ({
        url: /^\d+$/.test(String(body.id))
          ? `/api/Projects/UserGetProjectOrder?projectId=${body.id}`
          : `/api/Projects/UserGetProjectOrder?token=${encodeURIComponent(body.id)}`,
      }),
    }),
    // spec-28 — mints the opaque identifier for a project's confirmation page. Guarded server-side by
    // the same payer-scoped ownership rule as reading the order, so it 404s for anyone but the payer
    // (or a GlobalAdmin). Deliberately NOT delivered as a field on the project GET: that response also
    // goes to every target broadcaster of the delivery, which would hand them the very value the
    // opaque URL exists to withhold.
    getOrderConfirmationToken: build.query({
      query: (body) => ({
        url: `/api/Projects/GetOrderConfirmationToken?projectId=${body.id}`,
      }),
    }),
    getProjectFiles: build.query({
      query: (body) =>
        `/api/ProjectFiles/DataTablesSource${buildManagePageQueryString(
          body.columns,
          body.page,
          body.searchTerm,
          (body.page - 1) * body.limit,
          body.limit,
          body.orderColumnIndex,
          body.isDescSortDir
        )}`,
    }),
    downloadVersion: build.query({
      query: (body) => ({
        url: body.PreviewDownloadUrl,
      }),
    }),
    reprocessFileAdmin: build.mutation({
      query: ({ projectFileId }) => ({
        url: `/api/projectFiles/ReprocessProjectFile/${projectFileId}`,
        method: "GET",
      }),
      invalidatesTags: ["Projects"],
    }),
    getProjectByToken: build.query({
      query: ({ token }) => ({
        url: `/api/Projects/SharedVideoAdProject?id=${token}`,
        headers: {
          authorization: undefined,
        },
      }),
    }),
    adAgencyShortEdit: build.mutation({
      query: (body) => ({
        url: `/api/Projects/UserPutShortDelivery/${body.Id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Projects"],
    }),
    getBrandAndIndustryIdsForAdvertiser: build.query({
      query: (body) =>
        `/api/Projects/GetBrandAndIndustryIdsForAdvertiser?advertiserId=${body.advertiserId}&clientId=${body.clientId}`,
    }),
    // Campaigns view of the search-results page: same filter params as getProjects, but the backend
    // collapses the matched deliveries by campaign and server-paginates the distinct campaigns.
    // Returns { totalCount, take, page, campaings_data } (same campaign-card shape as the Campaigns page).
    getSearchCampaigns: build.query({
      query: (body) => ({
        url: `api/Campaigns/GetSearchCampaigns?itemsPerPage=${
          body?.limit || 10
        }&pageNumber=${body?.page - 1 || 0}&sortingOrder=${
          body?.sort || "num_desc"
        }&filterType=${body?.filterType || "name"}&filter=${
          body?.filter || ""
        }&statusName=${body?.statusName || ""}&projectType=${
          body?.projectType || "VideoAd"
        }&filterId=${body?.filterId || null}`,
      }),
      providesTags: ["Projects"],
    }),
  }),
});

export const {
  useGetProjectsQuery,
  useLazyGetProjectsQuery,
  useGetProjectByIdQuery,
  useCreateProjectMutation,
  useDeleteProjectMutation,
  useUpdateProjectMutation,
  useUpdateAndSendProjectMutation,
  useGetProjectsTotalCountQuery,
  useLazyGetProjectsTotalCountQuery,
  useGetManagePageProjectsQuery,
  useLazyGetManagePageProjectsQuery,
  useGetAllProjectsQuery,
  useLazyGetAllProjectsQuery,
  useUpdateProjectStatusMutation,
  useGetActivityLogQuery,
  useLazyGetActivityLogQuery,
  useGetDownloadLogQuery,
  useLazyGetDownloadLogQuery,
  useShareVideoMutation,
  useSetStartProjectEditingMutation,
  useSetCancelProjectEditingMutation,
  useSetHouseNumberMutation,
  useSendToFTPMutation,
  useSendToBroadcastersMutation,
  useSendForCustomerApprovalMutation,
  useCancelProjectMutation,
  useCloseProjectMutation,
  useChangeInternalNumberMutation,
  useSendToAdAgencyMutation,
  useCreateAndSendProjectMutation,
  useSetFileUploadedMutation,
  useDeleteUploadedFileMutation,
  useLazyGetProjectByIdQuery,
  useChangeProjectStatusMutation,
  useDownloadFileQuery,
  useLazyDownloadFileQuery,
  useSendFileToProcessQuery,
  useLazySendFileToProcessQuery,
  useGetNotificationsAmountQuery,
  useLazyGetNotificationsAmountQuery,
  useGetProjectCreationInfoQuery,
  useGetOrderDataQuery,
  useLazyGetOrderConfirmationTokenQuery,
  useGetProjectFilesQuery,
  useLazyGetProjectFilesQuery,
  useUserDeliverySendProjectQuery,
  useDownloadVersionQuery,
  useLazyDownloadVersionQuery,
  useReprocessFileAdminMutation,
  useGetProjectByTokenQuery,
  useRejectProjectMutation,
  useUpdateProjectShortMutation,
  useShareDeliveryMutation,
  useShareCampaignMutation,
  useAdAgencyShortEditMutation,
  useCreateAndSendDeliveryServiceMutation,
  useGetUnfinishedProjectsQuery,
  useLazyGetBrandAndIndustryIdsForAdvertiserQuery,
  useGetBrandAndIndustryIdsForAdvertiserQuery,
  useGetSharedVideoDataQuery,
  useLazyGetSharedVideoDataQuery,
  useGetSearchCampaignsQuery,
  useGetVideoShareLinkQuery,
} = projectsApi;
